export type PropertyAttractionInput = {
  category: string
  distance: string
  name: string
}

type ResolvedMapLocation = {
  district: string
  latitude: number
  locationLabel: string
  longitude: number
}

export function serializeNearbyAttractions(items: PropertyAttractionInput[]) {
  return items
    .map((item) => [item.name, item.category, item.distance].map((value) => String(value || "").trim()).filter(Boolean).join(" | "))
    .filter(Boolean)
    .join("\n")
}

export function parseNearbyAttractions(value: string) {
  return String(value || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [name = "", category = "", distance = ""] = line.split("|").map((part) => part.trim())
      return {
        name,
        category: category || "Attraction",
        distance: distance || "À proximité",
      }
    })
    .filter((item) => item.name)
}

function toRadians(value: number) {
  return (value * Math.PI) / 180
}

function getDistanceInMeters(
  fromLatitude: number,
  fromLongitude: number,
  toLatitude: number,
  toLongitude: number
) {
  const earthRadius = 6371000
  const dLat = toRadians(toLatitude - fromLatitude)
  const dLon = toRadians(toLongitude - fromLongitude)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(fromLatitude)) *
      Math.cos(toRadians(toLatitude)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)

  return 2 * earthRadius * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

function formatDistance(distanceInMeters: number) {
  if (distanceInMeters < 1000) {
    return `${Math.max(1, Math.round(distanceInMeters / 10) * 10)} m`
  }

  return `${(distanceInMeters / 1000).toFixed(1)} km`
}

function resolveAttractionCategory(tags: Record<string, string>) {
  if (tags.tourism) return tags.tourism
  if (tags.leisure) return tags.leisure
  if (tags.amenity) return tags.amenity
  if (tags.shop) return tags.shop
  return "Attraction"
}

function formatCategoryLabel(value: string) {
  return String(value || "Attraction")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

export async function detectCurrentPropertyLocation() {
  const coords = await new Promise<GeolocationCoordinates>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("La geolocalisation n'est pas disponible sur cet appareil."))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position.coords),
      () => reject(new Error("Impossible de detecter votre position actuelle.")),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    )
  })

  let locationLabel = ""
  let district = ""

  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.latitude}&lon=${coords.longitude}`
    )

    if (response.ok) {
      const payload = await response.json()
      const address = payload?.address || {}
      locationLabel = String(
        payload?.display_name ||
          address?.road ||
          address?.suburb ||
          address?.city ||
          ""
      ).trim()

      district = String(
        address?.suburb ||
          address?.neighbourhood ||
          address?.city_district ||
          address?.city ||
          ""
      ).trim()
    }
  } catch {
    // keep coordinates even if reverse geocoding fails
  }

  return {
    district,
    latitude: Number(coords.latitude.toFixed(6)),
    locationLabel,
    longitude: Number(coords.longitude.toFixed(6)),
  }
}

export async function fetchNearbyPropertyAttractions(
  latitude: number,
  longitude: number,
  radiusInMeters = 1500
) {
  const query = `
    [out:json][timeout:25];
    (
      node(around:${radiusInMeters},${latitude},${longitude})["tourism"];
      node(around:${radiusInMeters},${latitude},${longitude})["leisure"];
      node(around:${radiusInMeters},${latitude},${longitude})["amenity"];
      node(around:${radiusInMeters},${latitude},${longitude})["shop"];
      way(around:${radiusInMeters},${latitude},${longitude})["tourism"];
      way(around:${radiusInMeters},${latitude},${longitude})["leisure"];
      way(around:${radiusInMeters},${latitude},${longitude})["amenity"];
      way(around:${radiusInMeters},${latitude},${longitude})["shop"];
      relation(around:${radiusInMeters},${latitude},${longitude})["tourism"];
      relation(around:${radiusInMeters},${latitude},${longitude})["leisure"];
      relation(around:${radiusInMeters},${latitude},${longitude})["amenity"];
      relation(around:${radiusInMeters},${latitude},${longitude})["shop"];
    );
    out center tags;
  `

  const response = await fetch("https://overpass-api.de/api/interpreter", {
    body: query.trim(),
    method: "POST",
  })

  if (!response.ok) {
    throw new Error("Impossible de recuperer les attractions a proximite pour le moment.")
  }

  const payload = await response.json()
  const elements = Array.isArray(payload?.elements) ? payload.elements : []
  const seen = new Set<string>()

  return elements
    .map((element: any) => {
      const tags = element?.tags || {}
      const name = String(tags?.name || "").trim()
      const lat = Number(element?.lat ?? element?.center?.lat)
      const lon = Number(element?.lon ?? element?.center?.lon)

      if (!name || !Number.isFinite(lat) || !Number.isFinite(lon)) return null

      const category = formatCategoryLabel(resolveAttractionCategory(tags))
      const key = `${name.toLowerCase()}|${category.toLowerCase()}`
      if (seen.has(key)) return null
      seen.add(key)

      return {
        category,
        distance: formatDistance(getDistanceInMeters(latitude, longitude, lat, lon)),
        name,
      }
    })
    .filter(Boolean)
    .slice(0, 12) as PropertyAttractionInput[]
}

function isFiniteCoordinate(value: number) {
  return Number.isFinite(value) && Math.abs(value) <= 180
}

function toCoordinatePair(rawLat: string, rawLng: string): Pick<ResolvedMapLocation, "latitude" | "longitude"> | null {
  const latitude = Number(rawLat)
  const longitude = Number(rawLng)

  if (!isFiniteCoordinate(latitude) || !isFiniteCoordinate(longitude)) {
    return null
  }

  return {
    latitude: Number(latitude.toFixed(6)),
    longitude: Number(longitude.toFixed(6)),
  }
}

function extractCoordinatesFromText(value: string) {
  const patterns = [
    /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/,
    /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/,
    /(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/,
  ]

  for (const pattern of patterns) {
    const match = value.match(pattern)
    if (!match) continue

    const pair = toCoordinatePair(match[1], match[2])
    if (pair) return pair
  }

  return null
}

function deriveLocationLabelFromUrl(url: URL) {
  const queryLabel =
    url.searchParams.get("q") ||
    url.searchParams.get("query") ||
    url.searchParams.get("destination") ||
    url.searchParams.get("daddr") ||
    ""

  if (queryLabel && !/^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(queryLabel.trim())) {
    return decodeURIComponent(queryLabel).replace(/\+/g, " ").trim()
  }

  const dirMatch = url.pathname.match(/\/place\/([^/]+)/i)
  if (dirMatch?.[1]) {
    return decodeURIComponent(dirMatch[1]).replace(/\+/g, " ").trim()
  }

  return ""
}

export function parseGoogleMapsLocation(rawValue: string): ResolvedMapLocation | null {
  const value = String(rawValue || "").trim()
  if (!value) return null

  const directCoords = extractCoordinatesFromText(value)
  if (directCoords) {
    return {
      ...directCoords,
      district: "",
      locationLabel: "",
    }
  }

  let url: URL

  try {
    url = new URL(value)
  } catch {
    return null
  }

  const queryCandidates = [
    url.searchParams.get("q"),
    url.searchParams.get("query"),
    url.searchParams.get("ll"),
    url.searchParams.get("sll"),
    url.searchParams.get("center"),
    url.searchParams.get("destination"),
    url.searchParams.get("daddr"),
  ].filter(Boolean) as string[]

  for (const candidate of queryCandidates) {
    const pair = extractCoordinatesFromText(candidate)
    if (!pair) continue

    return {
      ...pair,
      district: "",
      locationLabel: deriveLocationLabelFromUrl(url),
    }
  }

  const urlCoords = extractCoordinatesFromText(`${url.pathname} ${url.hash}`)
  if (!urlCoords) return null

  return {
    ...urlCoords,
    district: "",
    locationLabel: deriveLocationLabelFromUrl(url),
  }
}
